// League detail: the heavy, per-league view loaded lazily via `adminLeagueDetail`.
// Snapshot data (members, current-round submission, tier split) renders instantly
// from the cached dashboard; the rounds/fixtures/divisions stream in on open.
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronLeft, RefreshCw } from 'lucide-react';
import { cap } from '../../lib/format';
import { LeagueMark } from '../../components/ui/bits';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { fetchAdminLeagueDetail, type AdminRoundDetail } from '../../lib/adminApi';
import {
  EmptyState,
  FixtureRow,
  MovementBadge,
  Panel,
  Pill,
  ProgressBar,
  RoundStatusPill,
  StatCard,
  TierBar,
  useAdminData
} from './parts';

type View = 'fixtures' | 'divisions';

export default function LeagueDetail() {
  const { leagueId = '' } = useParams();
  const { data: snapshot, password } = useAdminData();
  const snapRow = snapshot.leagues.find((l) => l.leagueId === leagueId) ?? null;

  const query = useQuery({
    queryKey: ['adminLeagueDetail', leagueId, password],
    queryFn: () => fetchAdminLeagueDetail(password, leagueId),
    enabled: !!leagueId && !!password,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false
  });

  const [view, setView] = useState<View>('fixtures');
  const [roundId, setRoundId] = useState<string | null>(null);

  const rounds = query.data?.rounds ?? [];
  const selectedRound = useMemo<AdminRoundDetail | null>(() => {
    if (rounds.length === 0) return null;
    const want = roundId ?? query.data?.league.currentRoundId ?? rounds[0].roundId;
    return rounds.find((r) => r.roundId === want) ?? rounds[0];
  }, [rounds, roundId, query.data]);

  const meta = query.data?.league;
  const headerName = meta?.name ?? snapRow?.name ?? 'League';
  const code = (meta?.shortCode ?? snapRow?.shortCode ?? headerName.slice(0, 3)).toUpperCase();
  const metaLine = [
    meta?.country ?? snapRow?.country,
    meta ? `Season ${meta.season}` : null,
    meta ? cap(meta.format) : null
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Link
        to="/admin/leagues"
        className="tap mb-4 inline-flex items-center gap-1 text-[13px] font-semibold text-ink2 hover:text-ink"
      >
        <ChevronLeft size={16} /> Leagues
      </Link>

      <div className="mb-5 flex items-start gap-3">
        <LeagueMark leagueId={leagueId} country={meta?.country ?? snapRow?.country} code={code} size={48} />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[24px] font-extrabold tracking-tight md:text-[28px]">{headerName}</h1>
          {metaLine && <div className="mt-0.5 truncate text-[13px] text-ink2">{metaLine}</div>}
        </div>
        {snapRow?.currentRoundId ? <Pill tone="accent">Active</Pill> : <Pill>Idle</Pill>}
      </div>

      {snapRow && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label="Members" value={snapRow.memberCount} />
            <StatCard label="Picks" value={snapRow.entriesCount} sub={`${snapRow.roundsCovered} rounds`} />
            <StatCard
              label="Submitted"
              value={
                snapRow.currentRound
                  ? `${snapRow.currentRound.submitted}/${snapRow.currentRound.eligibleMembers || '—'}`
                  : '—'
              }
              sub={snapRow.currentRound?.label}
            />
            <StatCard
              label="Submission"
              value={
                snapRow.currentRound?.submissionRate != null
                  ? `${Math.round(snapRow.currentRound.submissionRate * 100)}%`
                  : '—'
              }
              accent
            />
          </div>
          {snapRow.currentRound?.submissionRate != null && (
            <div className="mt-3">
              <ProgressBar value={snapRow.currentRound.submissionRate} />
            </div>
          )}

          <Panel title="Members by tier" className="mt-5" bodyClassName="px-4 py-4 md:px-5">
            <TierBar tiers={snapRow.tierCounts} />
          </Panel>
        </>
      )}

      <div className="mt-6">
        {query.isError ? (
          <Panel>
            <EmptyState>
              Couldn’t load this league’s rounds.{' '}
              <button type="button" onClick={() => void query.refetch()} className="font-semibold text-accent-ink">
                Retry
              </button>
            </EmptyState>
          </Panel>
        ) : !query.data ? (
          <div className="flex items-center gap-2 py-10 text-ink2">
            <RefreshCw size={16} className="animate-spin" />
            <span className="text-[14px] font-semibold">Loading rounds…</span>
          </div>
        ) : rounds.length === 0 ? (
          <EmptyState>No rounds yet.</EmptyState>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="relative">
                <select
                  value={selectedRound?.roundId}
                  onChange={(e) => setRoundId(e.target.value)}
                  className="num appearance-none rounded-full border border-black/10 bg-card py-2 pl-3.5 pr-9 text-[13px] font-semibold text-ink shadow-tile outline-none"
                >
                  {rounds.map((r) => (
                    <option key={r.roundId} value={r.roundId}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink3" />
              </div>
              {selectedRound && <RoundStatusPill status={selectedRound.status} />}
              <div className="w-full sm:ml-auto sm:w-[260px]">
                <SegmentedControl<View>
                  value={view}
                  onChange={setView}
                  layoutId="admin-league-detail-view"
                  options={[
                    { key: 'fixtures', label: `Fixtures${selectedRound ? ` · ${selectedRound.fixtureCount}` : ''}` },
                    { key: 'divisions', label: `Divisions${selectedRound ? ` · ${selectedRound.divisions.length}` : ''}` }
                  ]}
                />
              </div>
            </div>

            {selectedRound && view === 'fixtures' && (
              <Panel title={`${selectedRound.label} · ${selectedRound.entryCount} entries`}>
                {selectedRound.fixtures.length === 0 ? (
                  <EmptyState>No fixtures.</EmptyState>
                ) : (
                  <div className="divide-y divide-black/5">
                    {selectedRound.fixtures.map((f) => (
                      <FixtureRow key={f.fixtureId} f={f} />
                    ))}
                  </div>
                )}
              </Panel>
            )}

            {selectedRound &&
              view === 'divisions' &&
              (selectedRound.divisions.length === 0 ? (
                <EmptyState>No divisions for this round yet.</EmptyState>
              ) : (
                <div className="grid gap-4 lg:grid-cols-2">
                  {selectedRound.divisions.map((d) => (
                    <Panel key={d.groupId} title={`${cap(d.tierName)} · ${d.memberCount} players`}>
                      {d.standings.length === 0 ? (
                        <EmptyState>No standings yet.</EmptyState>
                      ) : (
                        <ul className="divide-y divide-black/5">
                          {d.standings.map((s) => (
                            <li key={s.userId} className="flex items-center gap-3 px-4 py-2.5 md:px-5">
                              <span className="num w-6 shrink-0 text-[13px] font-bold text-ink2">{s.rank}</span>
                              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
                                {s.displayName}
                              </span>
                              <MovementBadge movement={s.movement} />
                              <span className="num shrink-0 text-[14px] font-extrabold">{s.roundScore}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </Panel>
                  ))}
                </div>
              ))}
          </>
        )}
      </div>
    </>
  );
}
